import { ActivityEntityName, CodeStatus, EntityStatus, EntityStatusType, ItemCreator, Party, StatusPickerOption, canApproveInStatus, canEditItemInStatus, canSelectStatus } from '@jms/model';

/** What a row gate can see. `statusSet` is null while it loads, which every gate must read as "do not know". */
export interface ItemRowStatusFacts {
  stageWritable?: boolean;
  statusSet: StatusPickerOption[] | null;
  /** The stamp's id and system name, which is all a board row carries. */
  statusId?: string;
  systemName?: string;
  alreadyCancelled: boolean;
}

/**
 * Why Approve is disabled, or null when it is not.
 *
 * Resolved against the SET, never the stamp: a stamp carries no `isDefault`, so `canApproveInStatus`
 * read off a row answers yes for Draft, the one status the server refuses. A declined line is the
 * exception: its Inactive stamp is already on the row, so it is refused before the set has loaded.
 */
export function resolveApproveRefusal(facts: ItemRowStatusFacts): string | null {

  if (facts.stageWritable === false) return 'The stage is closed or finalised.';
  if (facts.alreadyCancelled) return 'The item is already declined.';
  // An unknown set leaves the command enabled, as an unprovable stage does for Delete: the menu is built
  // synchronously and the set arrives over HTTP, so refusing here disables the first open of every menu.
  if (!facts.statusSet) return null;

  const current = facts.statusSet.find(status => status._id === facts.statusId);

  return canApproveInStatus(current) ? null : `${current?.name ?? 'This status'} is not up for approval.`;
}

/** Why Request is disabled, or null when it is not. Office is the only party the item path ever resolves. */
export function resolveRequestRefusal(facts: ItemRowStatusFacts): string | null {

  if (facts.stageWritable === false) return 'The stage is closed or finalised.';
  if (facts.systemName === CodeStatus.Requested) return 'The item is already requested.';
  if (facts.alreadyCancelled) return 'The item is already declined.';
  if (!facts.statusSet) return null;

  const requested = facts.statusSet.find(status => status.systemName === CodeStatus.Requested);

  // Absent is unknown, not refused: the command resolves the status itself and says so if there is none.
  if (!requested) return null;

  return canSelectStatus(requested, Party.Office) ? null : 'Requested cannot be selected by your party.';
}

/** What the row ⋮ needs to decide whether Change Status and Cancel belong on a line. */
interface ItemRowStatusActionFacts {
  isPending: boolean;
  /** Jobs Manage plus the section's Edit; without it neither action is the user's to see. */
  canEditLine: boolean;
  deletionPrevented: boolean;
  /** The SORs tab, where Prevent deletion hides Delete only and Cancel is always offered (WBS-12433). */
  sorOnly?: boolean;
  /** The line's own set, null while it loads. */
  statusSet: StatusPickerOption[] | null;
  statusId?: string;
  /** Off the stamp, for a status the set no longer lists. */
  statusType?: EntityStatusType;
  raisedBy?: ItemCreator;
}

interface ItemRowStatusActions {
  changeStatus: boolean;
  cancel: boolean;
}

/**
 * Which status actions the row offers (WBS-12289).
 *
 * Both need a status the user's party may edit — the status's "Can be edited by", the same predicate the
 * editor and the write read. Cancel also needs an Active status, since it moves a line to an Inactive one,
 * and keeps riding on Prevent deletion on the Items tab: offered in Delete's place, never beside it. On the SORs tab
 * Prevent deletion hides Delete only and Cancel is always offered (Hung 2026-10-09, WBS-12433).
 *
 * A set still loading offers neither: the caller reads this inside a `computed` over the set, so the actions
 * appear once it arrives — and the server's single-line Cancel does not re-check the lock, so offering it
 * early would let a locked line be cancelled.
 */
export function resolveRowStatusActions(facts: ItemRowStatusActionFacts): ItemRowStatusActions {

  if (!facts.canEditLine) return { changeStatus: false, cancel: false };

  if (!facts.statusSet) return { changeStatus: false, cancel: false };

  const current = facts.statusSet.find(status => status._id === facts.statusId);
  const entityType = facts.isPending ? ActivityEntityName.ItemPending : ActivityEntityName.Item;
  const editable = !current || canEditItemInStatus({ ...current, entityType } as EntityStatus, Party.Office, facts.raisedBy);
  const active = (current?.statusType ?? facts.statusType) !== EntityStatusType.Inactive;

  return { changeStatus: editable, cancel: editable && active && (facts.deletionPrevented || facts.sorOnly === true) };
}
